import { Component, OnInit } from '@angular/core';
import { SubscriptionService } from '../../subscription.service';
import {Router, ActivatedRoute} from '@angular/router';
import { LoaderService } from '../../../loader.service';
import { getDateFromEpoch} from '../../../utils/utils'
import { AuthService } from '../../../auth.service';
import { ConfirmDialogService } from '../../../shared/confirm-dialog/confirm-dialog.service';
import { AlertDialogOptions, AlertDialogService } from '../../../shared/alert-dialog/alert-dialog.service';
import { subscriptionOperationFailureAlert, subscriptionUpdateSuccessAlert } from '../../subscription.create-alert';
import { SharedService } from '../../../shared/shared.service';
import { skipServiceHierarchyForMode } from '../../subscription.applicable-types';
import { AuthzAccessHintService } from '../../../shared/authz-access-hint/authz-access-hint.service';
import { AUTHZ_FEATURES } from '../../../constants/permissions';
import { permissionsForFeature } from '../../../utils/permission.utils';

@Component({
    selector:"app-view-subscription-rule",
    templateUrl: './view.component.html'
})

export class ViewSubscriptionRuleComponent implements OnInit{
    public ruleData: any = {};
    public canDeactivate = false;
    public authzAccessHint: { message: string; requestUrl: string } | null = null;
    public categoryName = '';
    public modeName = '';
    public isEntireMode = false;
    private categoryServices: { id: string, name: string }[] = [];

    constructor(
        private subscriptionService :SubscriptionService,
        private route: ActivatedRoute,
        private loaderService: LoaderService,
        private router: Router,
        private accessRights: AuthService,
        private dialogService: ConfirmDialogService,
        private alertDialogService: AlertDialogService,
        private sharedService: SharedService,
        private authzAccessHintService: AuthzAccessHintService,
    ){}

    async ngOnInit(){
        const editPermission = (permissionsForFeature(AUTHZ_FEATURES.SUBSCRIPTION, this.accessRights.getAuthzPanels()) || {}).edit;
        this.canDeactivate = this.accessRights.isAuthorizedFor(editPermission, AUTHZ_FEATURES.SUBSCRIPTION);
        this.authzAccessHint = await this.authzAccessHintService.getAccessHintData('subscription');
        this.loaderService.openLoading();
        const ruleId = this.route.snapshot.paramMap.get('id');
        try {
            const res: any = await this.subscriptionService.getSubscriptionRule(ruleId).toPromise();
            this.ruleData = res;
            this.ruleData.startDate = getDateFromEpoch(this.ruleData.startTime);
            this.ruleData.endDate = getDateFromEpoch(this.ruleData.endTime);
            this.ruleData.cityNames = this.uniqueValues(this.ruleData.cityNames);
            await this.resolveGrainApplicability();
        } catch (err) {
            console.log(err);
        } finally {
            this.loaderService.closeLoading();
        }
    }

    get showDeactivate() {
        return this.canDeactivate && this.ruleData && this.ruleData.active;
    }

    get cashbackValidity() {
        const list = this.ruleData && this.ruleData.cashbackValidity;
        return (list && list[0]) || null;
    }

    private uniqueValues(values) {
        if (!Array.isArray(values)) {
            return values;
        }
        const seen = {};
        return values.filter(value => {
            const key = String(value);
            if (seen[key]) {
                return false;
            }
            seen[key] = true;
            return true;
        });
    }

    displayOrNA(value) {
        return value === null || value === undefined || value === '' ? 'NA' : value;
    }

    get showGrainApplicability() {
        return this.isEntireMode || !!this.categoryApplicability;
    }

    get applicabilityText() {
        if (this.isEntireMode) {
            return 'Applicable for entire ' + (this.modeName || 'mode') + ' mode';
        }
        if (this.categoryApplicability) {
            const exclude = this.excludedServiceNames;
            return 'Applicable for entire ' + (this.categoryName || 'category') + ' category'
                + (exclude ? ', excluding: ' + exclude : '');
        }
        return '';
    }

    get categoryApplicability() {
        const categories = this.ruleData
            && this.ruleData.categoryLevelApplicability
            && this.ruleData.categoryLevelApplicability.categories;
        return (categories && categories[0]) || null;
    }

    get excludedServiceNames() {
        const excluded = (this.categoryApplicability && this.categoryApplicability.excludedServices) || [];
        if (!excluded.length) {
            return '';
        }
        const services = this.ruleData.services || [];
        const names = this.ruleData.serviceNames || [];
        return excluded.map(id => {
            const fromCategory = this.categoryServices.find(service => String(service.id) === String(id));
            if (fromCategory && fromCategory.name) {
                return fromCategory.name;
            }
            const index = services.findIndex(serviceId => String(serviceId) === String(id));
            return index !== -1 && names[index] ? names[index] : id;
        }).join(', ');
    }

    private async resolveGrainApplicability() {
        const cityId = (this.ruleData.cities && this.ruleData.cities[0]) || this.ruleData.city;
        const modeId = this.ruleData.mode;
        this.modeName = modeId || '';
        try {
            const skipRes: any = await this.subscriptionService.getSkipServiceHierarchyToggle().toPromise();
            const toggle = (skipRes && skipRes.data) || skipRes || {};
            this.isEntireMode = skipServiceHierarchyForMode(toggle, modeId, cityId);
        } catch (err) {
            this.isEntireMode = false;
        }
        if (!cityId || !modeId) {
            if (this.categoryApplicability) {
                this.categoryName = this.categoryApplicability.name || this.categoryApplicability.id;
            }
            return;
        }
        try {
            const modesRes: any = await this.sharedService.fetchModes(cityId).toPromise();
            const mode = ((modesRes && modesRes.data && modesRes.data.modes) || [])
                .find(item => String(item.id) === String(modeId));
            if (mode && mode.name) {
                this.modeName = mode.name;
            }
        } catch (err) {
            console.log(err);
        }
        if (this.isEntireMode) {
            return;
        }
        const category = this.categoryApplicability;
        if (!category) {
            return;
        }
        this.categoryName = category.name || category.id;
        try {
            const res: any = await this.sharedService.fetchSubscriptionServiceOptions(cityId, modeId).toPromise();
            this.categoryServices = (res && res.data && res.data.services) || [];
            const match = ((res && res.data && res.data.categories) || [])
                .find(item => String(item.id) === String(category.id));
            if (match && match.name) {
                this.categoryName = match.name;
            }
        } catch (err) {
            console.log(err);
        }
    }

    private showAlertBox(options: AlertDialogOptions) {
        this.alertDialogService.open(options);
    }

    async deactivateSubs() {
        const options = {
            title: 'Are you sure you want to deactivate the subscription?',
            message: `You are about to deactivate subscription ${this.ruleData.ruleName}`,
            cancelText: 'Cancel',
            confirmText: 'Yes, Proceed'
        };
        this.dialogService.open(options);
        this.dialogService.confirmed().subscribe(async (confirmed) => {
            if (!confirmed) {
                return;
            }
            try {
                const resp = await this.subscriptionService.updateRule(
                  this.ruleData.id || this.route.snapshot.paramMap.get('id'),
                  { active: false }
                ).toPromise();
                this.ruleData.active = false;
                this.showAlertBox(subscriptionUpdateSuccessAlert(resp));
            } catch (error) {
                this.showAlertBox(subscriptionOperationFailureAlert(error));
            }
        });
    }
}